import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { Router } from '@angular/router';
import { map, Observable } from 'rxjs';
import { PipingAndValvesDocumentService } from 'src/app/piping-valves/services/piping-and-valves-document.service';
import { PipingAndValvesNavigationService } from 'src/app/piping-valves/services/piping-and-valves-navigation.service';
import { UserService } from 'src/app/core/services/user.service';
import { TechnologyEnum } from 'src/app/shared/models/technology-enum';
import { EsraPipingAPIClient, PipeClassViewModel, ShareLinkResponse } from '../../models/autogenerated-piping';
import { ProductPanelHelper } from '../product-panel/product-panel-helper';

@Component({
  selector: 'app-pipeclass-panel',
  templateUrl: './pipeclass-panel.component.html',
  styleUrls: ['./pipeclass-panel.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class PipeClassPanelComponent implements OnInit {
  @Input()
  pipeClass: PipeClassViewModel;
  @Input()
  addWorkspaceShow: boolean;
  @Input()
  isAdminSearch: boolean;

  @Output()
  technology: TechnologyEnum = TechnologyEnum.PIPES;
  @Output()
  outputPipeClass: PipeClassViewModel;
  @Output()
  openPage: EventEmitter<string> = new EventEmitter<string>();
  @Output()
  pipeClassRetired: EventEmitter<void> = new EventEmitter<void>();

  isPipingAndValvesAdmin$: Observable<boolean>;

  constructor(
    private readonly productPanelHelper: ProductPanelHelper,
    private readonly pipingAndValvesNavigationService: PipingAndValvesNavigationService,
    private readonly pipingAndValvesDocumentService: PipingAndValvesDocumentService,
    private readonly router: Router,
    private readonly userService: UserService,
    private readonly esraPipingAPIClient: EsraPipingAPIClient
  ) {}

  /**
   * Builds the fetch function passed to `app-share-link-button` for this card's pipe class.
   * @param pipeClassId The pipe class id to build the share link for.
   * @returns A function returning an Observable of the absolute share URL.
   */
  getPipeClassShareUrlFn = (pipeClassId: string): () => Observable<string> =>
    () => this.esraPipingAPIClient.pipeClassShareLink(pipeClassId).pipe(
      map((response: ShareLinkResponse) => `${window.location.origin}/${(response.relativeUrl ?? '').replace(/^\/+/, '')}`)
    );

  getPipeClassPdfShareUrlFn = (recordId: string): () => Observable<string> =>
    () => this.esraPipingAPIClient.pipeClassPdfShareLink(recordId).pipe(
      map((response: ShareLinkResponse) => `${window.location.origin}/${(response.relativeUrl ?? '').replace(/^\/+/, '')}`)
    );

  ngOnInit(): void {
    this.isPipingAndValvesAdmin$ = this.userService.isPipingAndValvesAdminObservable();

    this.pipeClass.name = this.productPanelHelper.strReplaceHighlightHtml(
      this.pipeClass.name
    );

    this.pipeClass.businessUnits =
      this.productPanelHelper.arrReplaceHighlightHtml(
        this.pipeClass.businessUnits
      );

    this.pipeClass.application =
      this.productPanelHelper.strReplaceHighlightHtml(
        this.pipeClass.application
      );

    this.pipeClass.service.name =
      this.productPanelHelper.strReplaceHighlightHtml(
        this.pipeClass.service.name
      );

    this.pipeClass.nominalCorrosionAllowance.value =
      this.productPanelHelper.strReplaceHighlightHtml(
        this.pipeClass.nominalCorrosionAllowance.value
      );

    this.pipeClass.temperatureRange.minimum =
      this.productPanelHelper.strReplaceHighlightHtml(
        this.pipeClass.temperatureRange.minimum
      );

    this.pipeClass.temperatureRange.maximum =
      this.productPanelHelper.strReplaceHighlightHtml(
        this.pipeClass.temperatureRange.maximum
      );

    this.pipeClass.material.value =
      this.productPanelHelper.strReplaceHighlightHtml(
        this.pipeClass.material.value
      );

    this.pipeClass.valveTrim.value =
      this.productPanelHelper.strReplaceHighlightHtml(
        this.pipeClass.valveTrim.value
      );

    this.pipeClass.relatedVersions =
      this.productPanelHelper.strReplaceHighlightHtml(
        this.pipeClass.relatedVersions
      );

    this.pipeClass.sizeRange = this.productPanelHelper.strReplaceHighlightHtml(
      this.pipeClass.sizeRange
    );

    this.pipeClass.ratingClass.value =
      this.productPanelHelper.strReplaceHighlightHtml(
        this.pipeClass.ratingClass.value
      );
  }

  public stripEmphasisClassFromString(str: string): string {
    return ProductPanelHelper.RemoveHTML(str);
  }

  openPipeSummaryPage(): void {
    if (this.isAdminSearch) {
      this.openPage.emit(this.pipeClass.id);
    } else {
      this.pipingAndValvesDocumentService.openPDF(this.pipeClass.id, 'pp');
    }
  }
}
